// @flow

import type {InjectIntlProvidedProps} from 'react-intl';
import type {RootState} from '@reducers/types';
import type {DictionaryItem, Dispute, DisputeSort} from '../types';

import React, {useState} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {Table as UITable} from '@xsolla/uikit';
import classNames from 'classnames/bind';
import moment from 'moment';
import {compose} from 'recompose';
import {changeSort} from '@reducers/disputes/actions/table';
import {
  selectFeatureProtectUserIdField,
  selectProjects,
  selectSettingsMerchantID,
  selectTableDisputes,
  selectTableDisputesSort,
  selectTableFetching
} from '@reducers/disputes/selectors';
import EmptyMessage from '../components/empty-message';
import FetchingMessage from '../components/fetching-message';
import ViewSubmittedProof from '../components/view-submitted-proof';
import {prepareColumnGetter} from './columns';
import style from './style.css';

type Props = {
  +fetching: boolean,
  +disputes: Array<Dispute>,
  +projects: Array<DictionaryItem>,
  +sort: DisputeSort,
  +featureProtectUserIdField: boolean,
  +changeSort: (sort: DisputeSort) => void,
  +onOpenProofForm: (disputeId: number, transactionId: number) => void,
  +merchantId: number
} & InjectIntlProvidedProps;

const cx = classNames.bind(style);
const stylePrefix = 'dispute-table';

const Table = ({
  intl,
  fetching,
  disputes,
  projects,
  sort,
  changeSort,
  filterFormName,
  featureProtectUserIdField,
  onOpenProofForm,
  merchantId
}: Props) => {
  // Function to get display name for payment method with instance ID
  const getPaymentMethodDisplay = (dispute: Dispute): string => {
    const instance = dispute.instance;
    if (instance && instance.id) {
      // Use backend-provided instance name
      return instance.name || dispute.paymentMethod.name || '';
    }
    // Fallback to payment method name without instance ID
    return dispute.paymentMethod.name || '';
  };

  const [viewProofModal, setViewProofModal] = useState({
    isOpen: false,
    disputeId: null,
    transactionId: null,
    partnerEvidence: null
  });
  const getColumns = prepareColumnGetter(
    cx,
    stylePrefix,
    sort,
    changeSort,
    filterFormName,
    featureProtectUserIdField,
    onOpenProofForm,
    (disputeId, transactionId, partnerEvidence) => {
      setViewProofModal({
        isOpen: true,
        disputeId,
        transactionId,
        partnerEvidence
      });
    },
    merchantId
  );

  const handleCloseViewProofModal = () => {
    setViewProofModal({
      isOpen: false,
      disputeId: null,
      transactionId: null,
      partnerEvidence: null
    });
  };

  return (
    <>
      <UITable
        className={cx(`${stylePrefix}__wrapper`)}
        tableClassName={cx(stylePrefix)}
        scrollWrapperClassName={cx(
          `${stylePrefix}__scroll`,
          disputes.length !== 0 && `${stylePrefix}__scroll--filled`
        )}
        columns={getColumns(intl)}
        rows={disputes.map((dispute: Dispute) => {
          const project = projects.find(project => project.id === dispute.projectID);

          return {
            disputeId: dispute.id,
            transaction: {
              id: dispute.transaction.id,
              status: dispute.transaction.status,
              date: {
                local: moment(dispute.transaction.date)
                  .local()
                  .format('L LT'),
                cst: moment(dispute.transaction.date)
                  .utcOffset(-360)
                  .format('L LT')
              }
            },
            project: project,
            paymentMethod: getPaymentMethodDisplay(dispute),
            userDetails: {
              id: dispute.user.v1,
              name: dispute.user.email
            },
            countryCode: dispute.transaction.countryCode,
            transactionAmount: dispute.transaction.amount,
            fee: {
              fee: dispute.fee,
              isFinal: dispute.isFinal
            },
            incomingDate: moment(dispute.incomingDate)
              .startOf('day')
              .local()
              .format('L'),
            reason: dispute.reason && dispute.reason.name,
            type: dispute.type && dispute.type.name,
            result: dispute.result,
            partner_evidence_status: dispute.partner_evidence_status
          };
        })}
        stickyHead={true}
        fetching={fetching}
        renderEmptyMessage={() => (
          <EmptyMessage
            title={intl.messages['app.support.disputes.table.empty.title']}
            description={intl.messages['app.support.disputes.table.empty.description']}
          />
        )}
        renderFetchingMessage={() => <FetchingMessage />}
      />

      <ViewSubmittedProof
        isOpen={viewProofModal.isOpen}
        disputeId={viewProofModal.disputeId}
        transactionId={viewProofModal.transactionId}
        partnerEvidence={viewProofModal.partnerEvidence}
        onClose={handleCloseViewProofModal}
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  fetching: selectTableFetching(state),
  disputes: selectTableDisputes(state),
  projects: selectProjects(state),
  sort: selectTableDisputesSort(state),
  featureProtectUserIdField: selectFeatureProtectUserIdField(state),
  merchantId: selectSettingsMerchantID(state)
});

const mapDispatchToProps = {
  changeSort
};

const enhance = compose(connect(mapStateToProps, mapDispatchToProps), injectIntl);

export default enhance(Table);
